import { z } from 'zod';

/**
 * In-app notifications (Phase 2a M2): a row per person per event, read when the person opens
 * it or marks everything read. Kinds name the event so the shell can pick an icon; the link is
 * where the row goes when tapped.
 */
export const NotificationKindSchema = z.enum([
  'holiday_requested',
  'holiday_approved',
  'holiday_declined',
  'holiday_cancelled',
  'holiday_withdrawn',
  'holiday_booked',
  'holiday_nudge',
  'holiday_escalation',
  /** M2b: a swap or override changed the person's working days. */
  'working_day_changed',
  /** M2b: a booking's day count moved after a working-day change. */
  'holiday_recounted',
  /** Phase 2b M2: absence requests, decisions, recording, and the two reminders. */
  'absence_requested',
  'absence_approved',
  'absence_declined',
  'absence_withdrawn',
  'absence_recorded',
  'absence_cancelled',
  'holiday_days_returned',
  'rtw_due',
  'fit_note_due',
  /** Phase 2b M3: an absence rule (Bradford band or trigger) fired for someone. */
  'absence_rule_hit',
  /** Property (D60): issues, work orders, certificates, meter readings. */
  'property_issue',
  'property_job',
  'property_compliance',
  'property_meter',
  /** Fleet (D63): vehicle MOT/tax/insurance/service reminders and escalations. */
  'fleet_dates',
  /** Fleet M2: repairs, fines and incidents (cases), grey-fleet declarations. */
  'fleet_case',
  'fleet_grey',
  /** Safety (D68): accidents and near misses, RIDDOR deadlines, corrective actions. */
  'safety_incident',
  'safety_riddor',
  'safety_action',
  /** Safety (D68): a lone worker missed a check-in (or pressed panic), and its all clear. */
  'safety_lone_alarm',
  /** Safety (D68): a daily van check with a camera "No" or a missing camera photo. */
  'safety_camera',
  /** Operations (D71): a branch KPI failing past the escalation days, to the RM. */
  'oc_escalation',
  /** Operations (D71): an announcement reached you. */
  'announcement',
  /** Chat (Operations M2): someone @mentioned you, sent you a direct message, or replied in a record thread you follow. */
  'chat_mention',
  'chat_dm',
  'chat_thread',
  /** HR lifecycle (D74): approvals waiting, decisions, go-live, signatures, the daily digest. */
  'hr_approval',
  'hr_decision',
  'hr_lifecycle',
  'hr_digest',
]);
export type NotificationKind = z.infer<typeof NotificationKindSchema>;

export const NotificationSchema = z.object({
  id: z.uuid(),
  kind: NotificationKindSchema,
  title: z.string(),
  body: z.string().nullable(),
  link: z.string().nullable(),
  createdAt: z.iso.datetime(),
  readAt: z.iso.datetime().nullable(),
});
export type Notification = z.infer<typeof NotificationSchema>;

/** GET /notifications: newest first, capped. */
export const NotificationsResponseSchema = z.object({
  items: z.array(NotificationSchema),
  unread: z.number().int().min(0),
});
export type NotificationsResponse = z.infer<typeof NotificationsResponseSchema>;
